import React from 'react';

// Neutral fallback (only used for empty values).
const EMPTY_BG = '#eef0f3';
const EMPTY_TEXT = '#5b6472';

/**
 * Deterministic, pleasant color from a string — so labels that aren't in a
 * curated palette (e.g. "Swim adult", "Swim - single*") still get a consistent,
 * distinct chip color instead of all rendering the same flat gray.
 */
const hueFromText = (text: string): number => {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 360;
  }
  return hash;
};

type ColorChipProps = {
  label?: string | number | null;
  /** Curated palette map from colorHandler (keys `X` and `X_BG`). Optional. */
  palette?: Record<string, string>;
  /** Key into the palette; defaults to the label text. */
  paletteKey?: string;
  /** Text shown when the value is empty. */
  emptyLabel?: string;
  /** Optional leading icon (inherits the chip's accent color). */
  icon?: React.ReactNode;
  /** Optional inline style for context-specific sizing/alignment. */
  style?: React.CSSProperties;
};

/**
 * Consistent pill/chip used across the registration, report, assessment and
 * trial tables. Hugs its content (not full-cell width), uses the curated
 * palette color when available, otherwise a stable auto-color derived from the
 * label.
 */
const ColorChip: React.FC<ColorChipProps> = ({
  label,
  palette,
  paletteKey,
  emptyLabel = 'None',
  icon,
  style,
}) => {
  const isEmpty = label === null || label === undefined || label === '';
  const text = isEmpty ? emptyLabel : String(label);
  const key = paletteKey ?? (typeof label === 'string' ? label : '');

  let backgroundColor = EMPTY_BG;
  let color = EMPTY_TEXT;

  if (!isEmpty && palette && key && palette[key]) {
    backgroundColor = palette[`${key}_BG`] || palette[key];
    color = palette[key];
  } else if (!isEmpty && key) {
    const hue = hueFromText(key);
    backgroundColor = `hsl(${hue}, 70%, 93%)`;
    color = `hsl(${hue}, 55%, 32%)`;
  }

  return (
    <span
      style={{
        backgroundColor,
        color,
        // Subtle border derived from the accent color. Falls back to a solid
        // accent border on browsers without color-mix — still looks fine.
        borderColor: `color-mix(in srgb, ${color} 28%, transparent)`,
        ...style,
      }}
      className="inline-flex items-center gap-1 border rounded-md px-1 py-0 text-xs font-normal leading-5 whitespace-nowrap"
    >
      {!isEmpty && icon}
      {text}
    </span>
  );
};

export default ColorChip;
